'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

type SrpNavModeValue = {
  // Bumped each time a server-driven navigation starts, so results-only loading
  // is tied to real navigations instead of incidental request-identity changes.
  beginNavigation: () => void;
  facetsHydrated: boolean;
  isServerDriven: boolean;
  markFacetsHydrated: () => void;
  navToken: number;
};

const noop = () => {};

// True only under cars-rsc: lets shared SRP hooks skip client-side fetching (islands
// own the data) and route filter changes through the App Router, not pushState.
const SrpNavModeContext = createContext<SrpNavModeValue>({
  beginNavigation: noop,
  facetsHydrated: false,
  isServerDriven: false,
  markFacetsHydrated: noop,
  navToken: 0
});

export function SrpNavModeProvider({ children }: React.PropsWithChildren) {
  const [navToken, setNavToken] = useState(0);
  const beginNavigation = useCallback(
    () => setNavToken(token => token + 1),
    []
  );

  const [facetsHydrated, setFacetsHydrated] = useState(false);
  const markFacetsHydrated = useCallback(() => setFacetsHydrated(true), []);

  const value = useMemo(
    () => ({
      beginNavigation,
      facetsHydrated,
      isServerDriven: true,
      markFacetsHydrated,
      navToken
    }),
    [beginNavigation, facetsHydrated, markFacetsHydrated, navToken]
  );

  return (
    <SrpNavModeContext.Provider value={value}>
      {children}
    </SrpNavModeContext.Provider>
  );
}

export function useIsServerDriven(): boolean {
  return useContext(SrpNavModeContext).isServerDriven;
}

// Bumps the navigation token; call immediately before a server-driven
// router.push so hooks can react to a genuine navigation.
export function useBeginServerNavigation(): () => void {
  return useContext(SrpNavModeContext).beginNavigation;
}

export function useServerNavToken(): number {
  return useContext(SrpNavModeContext).navToken;
}

// True once FacetHydrator pushes backend-resolved facets; distinct from
// facetData.isLoaded, which flips true with baseline-only data first.
export function useFacetsHydrated(): boolean {
  return useContext(SrpNavModeContext).facetsHydrated;
}

export function useMarkFacetsHydrated(): () => void {
  return useContext(SrpNavModeContext).markFacetsHydrated;
}

export default SrpNavModeContext;
